import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Id } from '@turf/turf';
import { combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { DEVICE_STATUS } from '../../../app/class/Device';
import { Unit } from '../../../app/database/models';
import { MapService } from '../../../app/services/map.service';
import { ClustersDevicesLayer } from '../../../app/services/map/cluster-devices-layer.service';
import { PreferenceService } from '../../../app/services/preference.service';
import UnitsService from '../../../app/services/units.service';
import { DevicesManagerService, DeviceType } from '../../services/devices-manager.service';
import { GlobalSearchService } from '../../services/global-search.service';
import { DevicesLayerService } from '../../services/map/devices-layer.service';
import { PermissionService } from '../../services/permission.service';
import { DeviceFilterComponent } from '../devices/device-filter/device-filter.component';
import { DEVICE_HISTORY } from '../devices/devices-routing.constants';
import { ContainerComponent } from '../layout/container/container.component';
import { LayoutComponent } from '../layout/layout.component';
import { LeftPanelComponent } from '../layout/left-panel/left-panel.component';
import { SubNavigationItemComponent } from '../layout/sub-navigation/sub-navigation-item/sub-navigation-item.component';
import { SubNavigationComponent } from '../layout/sub-navigation/sub-navigation.component';
import { TopBarComponent } from '../layout/top-bar/top-bar.component';
import { SearchbarComponent } from '../searchbar/searchbar.component';
import { AlertComponent } from '../shared/card/alert/alert.component';
import { DeviceCard2Component } from '../shared/card/device-card-2/device-card-2.component';

@Component({
  selector: 'map-tab',

  imports: [
    TranslatePipe,
    ContainerComponent,
    DeviceCard2Component,
    MatProgressBarModule,
    AlertComponent,
    SearchbarComponent,
    ReactiveFormsModule,
    ScrollingModule,
    LayoutComponent,
    TopBarComponent,
    SubNavigationComponent,
    SubNavigationItemComponent,
    LeftPanelComponent,
    DeviceFilterComponent,
    MatIcon,
    MatBadgeModule,
    MatButtonModule,
  ],
  templateUrl: './map-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './map-tab.component.scss',
})
export class MapTabComponent implements OnInit {
  private _router = inject(Router);
  private _globalSearchService = inject(GlobalSearchService);
  private _devicesLayerService = inject(DevicesLayerService);
  private _clustersDevicesLayer = inject(ClustersDevicesLayer);
  private _mapService = inject(MapService);
  public permissionService = inject(PermissionService);
  private _devicesManagerService = inject(DevicesManagerService);
  private _unitsService = inject(UnitsService);
  private _preferenceService = inject(PreferenceService);
  private destroyRef = inject(DestroyRef);
  private _visibleDevices$ = combineLatest([
    toObservable(this._devicesLayerService.visiblePoints),
    toObservable(this._clustersDevicesLayer.$visiblePoints),
  ]).pipe(
    map(([devices, clustersLayerDevices]) => {
      switch (this._mapService.clusterMode) {
        case 'inactive':
        case 'polygon':
          return devices;
        case 'circle':
          return clustersLayerDevices;
      }
    })
  );
  public filtersCount = 0;
  public $displayFilters = signal(false);

  public formGroup = new FormGroup({ searchValue: new FormControl<string>('') });
  public filtersFormGroup = new FormGroup({
    typeFilter: new FormControl<DeviceType[]>([]),
    statusFilter: new FormControl<(keyof typeof DEVICE_STATUS)[]>([]),
    unitsFilter: new FormControl<Unit[]>([]),
  });

  constructor() {
    effect(async () => {
      await this._initFilters();
    });
  }
  async ngOnInit(): Promise<void> {
    this.filtersFormGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value.statusFilter.length) this._handleTypeFilterChange(value.typeFilter);

      this._devicesManagerService.setFilters({ filters: this.filtersFormGroup.value });
      if (this._mapService.$devicesTracking()) {
        this._devicesLayerService.fit({ addPadding: true });
      }

      this._updateFiltersCount();
    });
  }

  private _handleTypeFilterChange = (typeFilter: DeviceType[]): void => {
    const isBeaconOnly = typeFilter.length === 1 && typeFilter[0] === 'beacon';

    if (!isBeaconOnly) return;

    this.filtersFormGroup.patchValue({ statusFilter: [] });
  };

  private _updateFiltersCount() {
    this.filtersCount = Object.values(this.filtersFormGroup.value).filter(
      value => Array.isArray(value) && value.length > 0
    ).length;
  }

  get search(): string {
    return this.formGroup.get('searchValue').value;
  }

  private searchResult$ = this.formGroup.controls.searchValue.valueChanges.pipe(
    startWith(this.formGroup.value.searchValue),
    debounceTime(200),
    distinctUntilChanged(),
    map(value => {
      if (value) {
        const results = this._globalSearchService.search(value);
        return results.devices ? results.devices.map(device => device.uid) : null;
      }
      return null;
    })
  );

  private async _initFilters() {
    const unitsIds = this._preferenceService.getUnitsFilter();

    let units: Unit[] = [];
    // // Set filters control
    if (unitsIds.length) {
      // noneOf because the ids stored in preferences was the ids we don't want to see on the map
      units = await this._unitsService.table.where('uid').noneOf(unitsIds).toArray();
    }

    this.filtersFormGroup.setValue({
      typeFilter: this._preferenceService.getDeviceTypeFilter(),
      statusFilter: this._preferenceService.getMovingStatusFilter(),
      unitsFilter: units,
    });

    this._updateFiltersCount();
  }

  public $visibleResult = toSignal(
    combineLatest([this._visibleDevices$, this.searchResult$]).pipe(
      distinctUntilChanged(),
      map(([visibleDevices, searchResults]) => {
        if (!searchResults) return visibleDevices;

        const matchingDevices = visibleDevices.filter(device => searchResults.includes(device.id));

        return matchingDevices;
      })
    )
  );

  /** Fixed row height (px) required by the virtual scroll. */
  public readonly deviceCardHeight = 52;

  public trackById = (_index: number, item: { id: Id }): Id => item.id;

  public goToHistory(uid: Id) {
    const device = this._devicesManagerService.get(uid);
    this._router.navigate([DEVICE_HISTORY(device.uid_parent, device.uid)], { queryParams: { view: 'map' } });
  }

  public toggleFilters() {
    this.$displayFilters.update(v => !v);
  }
}
